<template>
  <div class="career-ai-launcher">
    <button
      type="button"
      class="career-ai-fab"
      :id="`career-ai-fab-${placement}`"
      aria-label="AIチャットを開く"
      @click="open"
    >
      <span class="career-ai-fab__icon" aria-hidden="true">✦</span>
      <span class="career-ai-fab__label">AIチャット</span>
    </button>

    <!-- 初回 mount 後は v-show だけで開閉し、会話を保持する -->
    <transition name="career-ai-modal">
      <div
        v-if="mounted"
        v-show="visible"
        class="career-ai-modal-overlay"
        @click.self="visible = false"
      >
        <div class="career-ai-modal-window">
          <CareerAI
            :show-service-menu="true"
            :uuid="uuid"
            :session-id="sessionId"
            :initial-mode="initialMode"
            :remaining="remaining"
            :daily-limit="dailyLimit"
            @close="visible = false"
            @clear="restartSession"
          />
        </div>
      </div>
    </transition>

    <CareerAIConsentDialog
      :visible="consentVisible"
      :submitting="consentSubmitting"
      @accept="acceptConsent"
      @decline="declineConsent"
    />

    <!-- トップには LoginDialog が無いので、未ログインで押されたときだけここで持つ。
         LoginDialog を持つページに置くときは二重に開くため、この仕組みを見直すこと -->
    <LoginDialog v-if="loginDialogMounted" />
  </div>
</template>

<script setup lang="ts">
/**
 * AIチャット（ヤギコンシェルジュ）のフローティングボタンと、開いたときのチャット窓。
 * マイページ以外のページにボットを置くための入口。未ログインならログインダイアログを
 * 出し、ログインできたらそのまま開く。
 */
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { Message } from 'element-ui'
import CareerAI from '@/components/career-ai/CareerAI.vue'
import CareerAIConsentDialog from '@/components/CareerAIConsentDialog.vue'
import LoginDialog from '@/components/LoginDialog.vue'
import careerAIClient from '@/services/career-ai-client'
import { useAuth } from '@/composables/useAuth'
import { createTrackEvent } from '@/composables/ai/useCareerAiTracking'
import { emitter, emitOpenLoginDialog, LOGIN_SUCCESS } from '@/plugins/emitter'

const props = defineProps<{
  /** 設置場所。計測の placement と、ボタン id の接尾辞に使う */
  placement: string
}>()

const DEFAULT_LIMIT = 30

const { user } = useAuth()

const visible = ref(false)
const mounted = ref(false)
const uuid = ref('')
const sessionId = ref('')
const initialMode = ref('concierge')
const remaining = ref(DEFAULT_LIMIT)
const dailyLimit = ref(DEFAULT_LIMIT)
const consentVisible = ref(false)
const consentSubmitting = ref(false)
const loginDialogMounted = ref(false)
/** ログイン要求で中断した起動。ログイン成功後に続きを行う */
let pendingOpen = false

const trackEvent = createTrackEvent(
  () => uuid.value,
  () => sessionId.value
)

function notifyError(message?: string) {
  Message({
    type: 'error',
    message:
      message ||
      'ヤギコンシェルジュの起動に失敗しました。少し時間をおいてお試しください。'
  })
}

function applyStart(res: {
  session_id: string
  current_mode?: string
  remaining?: number
  daily_limit?: number
}) {
  sessionId.value = res.session_id
  initialMode.value = res.current_mode || 'concierge'
  if (typeof res.remaining === 'number') remaining.value = res.remaining
  if (typeof res.daily_limit === 'number') dailyLimit.value = res.daily_limit
}

async function startAndShow() {
  // chatStart は残メッセージ数を消費するので、同じページ内では再発行しない
  if (mounted.value && sessionId.value) {
    visible.value = true
    return
  }
  try {
    const res = await careerAIClient.chatStart(uuid.value)
    if (res && res.success) {
      applyStart(res)
      mounted.value = true
      visible.value = true
    } else {
      notifyError(res && res.message)
    }
  } catch (e: any) {
    if (e && e.status === 403 && e.code === 'CONSENT_REQUIRED') {
      consentVisible.value = true
    } else {
      notifyError(e && e.message)
    }
  }
}

async function openForUser(uid: string) {
  uuid.value = uid
  try {
    const consent = await careerAIClient.getConsent(uid)
    if (!consent || !consent.consented) {
      consentVisible.value = true
      return
    }
  } catch (_) {
    consentVisible.value = true
    return
  }
  await startAndShow()
}

async function openLogin() {
  pendingOpen = true
  // mount 直後でないと OPEN_LOGIN_DIALOG の購読が始まっていない
  loginDialogMounted.value = true
  await nextTick()
  emitOpenLoginDialog({ trigger: `career_ai_fab_${props.placement}` })
}

function open() {
  const uid = user.value?.uid
  trackEvent('career_ai_fab_click', {
    placement: props.placement,
    is_logged_in: !!uid
  })
  if (!uid) {
    openLogin()
    return
  }
  openForUser(uid)
}

function onLoginSuccess() {
  if (!pendingOpen) return
  pendingOpen = false
  const uid = user.value?.uid
  if (uid) openForUser(uid)
}

async function restartSession() {
  // 旧セッションを終了して新しく発行する。session_id が変わると CareerAI が履歴を初期化する
  try {
    await careerAIClient.chatEnd(uuid.value)
  } catch (_) {
    // 終了に失敗しても新規発行は試みる
  }
  try {
    const res = await careerAIClient.chatStart(uuid.value)
    if (res && res.success) {
      applyStart(res)
      trackEvent('career_ai_clear_history', { placement: props.placement })
    } else {
      notifyError(res && res.message)
    }
  } catch (e: any) {
    notifyError(e && e.message)
  }
}

async function acceptConsent() {
  consentSubmitting.value = true
  trackEvent('career_ai_consent_accept', { placement: props.placement })
  try {
    await careerAIClient.setConsent(uuid.value, true)
    consentVisible.value = false
    await startAndShow()
  } catch (e: any) {
    notifyError(e && e.message)
  } finally {
    consentSubmitting.value = false
  }
}

function declineConsent() {
  trackEvent('career_ai_consent_decline', { placement: props.placement })
  consentVisible.value = false
}

onMounted(() => emitter.on(LOGIN_SUCCESS, onLoginSuccess))
onBeforeUnmount(() => emitter.off(LOGIN_SUCCESS, onLoginSuccess))
</script>

<style lang="scss" scoped>
$deep-green: #004d40;
$deep-green-hover: #00695c;

.career-ai-fab {
  position: fixed;
  right: 28px;
  bottom: calc(28px + env(safe-area-inset-bottom, 0px));
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 99px;
  background: $deep-green;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 20px rgb(0 77 64 / 45%);
  transition:
    transform 0.2s,
    box-shadow 0.2s,
    background 0.2s;

  /* グローバルの button:hover { opacity: 0.5 } を打ち消す */
  &:hover {
    opacity: 1;
    transform: translateY(-2px);
    background: $deep-green-hover;
    box-shadow: 0 6px 24px rgb(0 77 64 / 55%);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -4px;
  }

  @media screen and (width <= 767px) {
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
}

.career-ai-fab__icon {
  font-size: 16px;
}

.career-ai-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 20px;
  background: rgb(0 0 0 / 30%);

  @media screen and (width <= 767px) {
    justify-content: center;
    padding: 0;
  }
}

.career-ai-modal-window {
  width: 380px;
  height: 620px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgb(0 0 0 / 30%);

  @media screen and (width <= 767px) {
    width: 100%;
    height: 88vh;
    max-height: none;
    border-radius: 16px 16px 0 0;
  }
}

.career-ai-modal-enter-active,
.career-ai-modal-leave-active {
  transition: opacity 0.25s ease;
}

.career-ai-modal-enter-active .career-ai-modal-window,
.career-ai-modal-leave-active .career-ai-modal-window {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.career-ai-modal-enter,
.career-ai-modal-leave-to {
  opacity: 0;
}

.career-ai-modal-enter .career-ai-modal-window,
.career-ai-modal-leave-to .career-ai-modal-window {
  transform: translateY(20px);
  opacity: 0;
}
</style>
